import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CategoryService, Category, Subcategory, ProductCategory } from "@/lib/category-service";
import { ProductService, Product as DbProduct } from "@/lib/product-service";
import { Product } from "@/polymet/data/machinery-products";
import { getProductThumbnail, preloadProductThumbnails } from "@/lib/cloudinary-helpers";
import BreadcrumbNavigation from "@/polymet/components/breadcrumb-navigation";
import ProductGrid from "@/polymet/components/product-grid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { FilterIcon, SearchIcon, SlidersIcon, XIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import PageLayout from "@/polymet/components/page-layout";
import { useMediaQuery } from "react-responsive";
import { supabase } from "@/lib/supabase";
import { useAnalytics } from "@/polymet/hooks/use-analytics";

export default function ProductListingPage() {
  const {
    categoryId = "",
    subCategoryId = "",
    productCategoryId = "",
  } = useParams();

  const [category, setCategory] = useState<Category | null>(null);
  const [subCategory, setSubCategory] = useState<Subcategory | null>(null);
  const [productCategory, setProductCategory] = useState<ProductCategory | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [selectedManufacturers, setSelectedManufacturers] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [availability, setAvailability] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000000]);
  const [sortBy, setSortBy] = useState("featured");

  const isMobile = useMediaQuery({ query: '(max-width: 640px)' });
  const isTablet = useMediaQuery({ query: '(max-width: 1024px)' });

  // Get unique manufacturers and years from products
  const [manufacturers, setManufacturers] = useState<string[]>([]);
  const [years, setYears] = useState<(number | string)[]>([]);

  // Services
  const categoryService = new CategoryService();
  const productService = new ProductService();
  const { trackCategoryVisit } = useAnalytics();

  // Transform database product to UI product format
  const transformDbProductToProduct = (dbProduct: any): Product & { slug: string } => {
    // The ProductService now formats images properly, so we can use them directly
    const images = Array.isArray(dbProduct.images) && dbProduct.images.length > 0 
      ? dbProduct.images 
      : [`https://picsum.photos/800/600?random=${dbProduct.id}`];

    return {
      id: dbProduct.id,
      slug: dbProduct.slug || dbProduct.id,
      name: dbProduct.name || '',
      categoryId: dbProduct.category_id || '',
      subCategoryId: dbProduct.subcategory_id || '',
      productCategoryId: dbProduct.product_category_id || '',
      condition: (dbProduct.condition as "New" | "Used" | "Refurbished") || "Used",
      price: Number(dbProduct.price) || 0,
      previousPrice: dbProduct.previous_price ? Number(dbProduct.previous_price) : undefined,
      currency: dbProduct.currency || 'INR',
      description: dbProduct.description || '',
      features: [],
      specifications: [],
      images: images,
      year: dbProduct.year,
      manufacturer: dbProduct.manufacturer || '',
      model: dbProduct.model || '',
      serialNumber: dbProduct.serial_number,
      hoursUsed: dbProduct.hours_used,
      location: dbProduct.location || '',
      seller: {
        id: dbProduct.supplier_id || '',
        name: dbProduct.supplier_business_name || 'Unknown Seller',
        logo: dbProduct.supplier_logo_url,
        location: dbProduct.location || '',
        rating: Number(dbProduct.supplier_rating) || 0,
        verified: Boolean(dbProduct.supplier_verified),
        joinedDate: new Date().toISOString(),
      },
      brand: dbProduct.manufacturer ? {
        id: dbProduct.manufacturer.toLowerCase().replace(/\s+/g, '-'),
        name: dbProduct.manufacturer,
        logo_url: undefined,
        verified: false
      } : undefined,
      category: dbProduct.category_id || '',
      subCategory: dbProduct.subcategory_id || '',
      productCategory: dbProduct.product_category_id || '',
      tags: [],
      rating: Number(dbProduct.rating) || 0,
      reviewCount: Number(dbProduct.review_count) || 0,
      inStock: Boolean(dbProduct.in_stock),
      financing: Boolean(dbProduct.financing_available),
      deliveryAvailable: Boolean(dbProduct.delivery_available),
      deliveryFee: dbProduct.delivery_fee ? Number(dbProduct.delivery_fee) : undefined,
      featured: dbProduct.featured || false,
      // Additional fields based on the database structure
      supplier_business_name: dbProduct.supplier_business_name || '',
      supplier_logo_url: dbProduct.supplier_logo_url || '',
      supplier_rating: Number(dbProduct.supplier_rating) || 0,
      supplier_verified: Boolean(dbProduct.supplier_verified),
      supplier_review_count: Number(dbProduct.supplier_review_count) || 0,
      supplier_response_rate: dbProduct.supplier_response_rate || 0,
      supplier_mobile_number: dbProduct.supplier_mobile_number || '',
      // Required missing properties
      reviews: [],
      listedDate: dbProduct.created_at || new Date().toISOString(),
      warranty: dbProduct.warranty,
    } as Product & { slug: string };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Find the category by slug
        const categories = await categoryService.getCategories();
        const foundCategory = categories.find(cat => cat.slug === categoryId);
        
        if (!foundCategory) {
          setError("Category not found");
          setLoading(false);
          return;
        }
        setCategory(foundCategory);

        // Find the subcategory by slug
        const subcategories = await categoryService.getSubcategoriesByCategoryId(foundCategory.id);
        const foundSubCategory = subcategories.find(subCat => subCat.slug === subCategoryId);
        
        if (!foundSubCategory) {
          setError("Subcategory not found");
          setLoading(false);
          return;
        }
        setSubCategory(foundSubCategory);

        // Find the product category by slug
        const productCategories = await categoryService.getProductCategoriesBySubcategoryId(foundSubCategory.id);
        const foundProductCategory = productCategories.find(prodCat => prodCat.slug === productCategoryId);
        
        if (!foundProductCategory) {
          setError("Product category not found");
          setLoading(false);
          return;
        }
        setProductCategory(foundProductCategory);

        // Fetch products for this product category with fallback strategies
        let dbProducts: DbProduct[] = [];
        
        try {
          // Primary strategy: Fetch by exact product category ID
          dbProducts = await productService.getProductsByProductCategoryId(foundProductCategory.id);
          console.log(`🔍 Primary fetch: Found ${dbProducts.length} products for product category "${foundProductCategory.name}" (ID: ${foundProductCategory.id})`);
          
          // If no products found, try fallback strategies
          if (dbProducts.length === 0) {
            console.log('🔄 No products found with primary strategy, trying fallbacks...');
            
            // Fallback 1: Fetch by subcategory ID
            console.log('🔍 Fallback 1: Fetching products by subcategory...');
            const subcategoryProducts = await productService.getProductsBySubcategoryId(foundSubCategory.id);
            console.log(`📊 Found ${subcategoryProducts.length} products in subcategory "${foundSubCategory.name}"`);
            
            // Filter subcategory products to match our product category if possible
            const filteredSubcategoryProducts = subcategoryProducts.filter(product => 
              !product.product_category_id || product.product_category_id === foundProductCategory.id
            );
            
            if (filteredSubcategoryProducts.length > 0) {
              dbProducts = filteredSubcategoryProducts;
              console.log(`✅ Using ${dbProducts.length} products from subcategory fallback`);
            } else {
              // Fallback 2: Fetch by category ID
              console.log('🔍 Fallback 2: Fetching products by category...');
              const categoryProducts = await productService.getProductsByCategoryId(foundCategory.id);
              console.log(`📊 Found ${categoryProducts.length} products in category "${foundCategory.name}"`);
              
              // Filter category products to match our subcategory/product category if possible
              const filteredCategoryProducts = categoryProducts.filter(product => 
                (!product.subcategory_id || product.subcategory_id === foundSubCategory.id) &&
                (!product.product_category_id || product.product_category_id === foundProductCategory.id)
              );
              
              if (filteredCategoryProducts.length > 0) {
                dbProducts = filteredCategoryProducts;
                console.log(`✅ Using ${dbProducts.length} products from category fallback`);
              } else {
                // Fallback 3: Use all products from the category
                dbProducts = categoryProducts;
                console.log(`⚠️ Using all ${dbProducts.length} products from category (no filtering)`);
              }
            }
          }
          
          // Final check: If still no products, check if there are any products at all in the system
          if (dbProducts.length === 0) {
            console.log('🔍 Final fallback: Checking for any products in the system...');
            const allProducts = await productService.getAllProducts();
            console.log(`📊 Total products in system: ${allProducts.length}`);
            
            if (allProducts.length > 0) {
              console.log('⚠️ Products exist but none match the category hierarchy. This suggests a data integrity issue.');
              console.log('💡 Consider updating product category assignments in the database.');
              
              // For debugging, show some sample products
              const sampleProducts = allProducts.slice(0, 5);
              console.log('📝 Sample products in system:');
              sampleProducts.forEach((product, index) => {
                console.log(`  ${index + 1}. ${product.name} (slug: ${product.slug})`);
                console.log(`     Category: ${product.category_id}, Subcategory: ${product.subcategory_id}, Product Category: ${product.product_category_id}`);
              });
            }
          }
          
        } catch (error) {
          console.error('❌ Error fetching products:', error);
          // Try to get any products as a last resort
          try {
            const allProducts = await productService.getAllProducts();
            const categoryMatchProducts = allProducts.filter(product => 
              product.category_id === foundCategory.id ||
              product.subcategory_id === foundSubCategory.id ||
              product.product_category_id === foundProductCategory.id
            );
            dbProducts = categoryMatchProducts;
            console.log(`🆘 Emergency fallback: Using ${dbProducts.length} products with partial category match`);
          } catch (emergencyError) {
            console.error('💥 Emergency fallback failed:', emergencyError);
          }
        }
        
        const transformedProducts = dbProducts.map(transformDbProductToProduct);
        
        setProducts(transformedProducts);
        setFilteredProducts(transformedProducts);

        // Preload thumbnails for better performance
        const originalImageUrls = dbProducts
          .flatMap((product: any) => product.images || [])
          .filter(Boolean);
        if (originalImageUrls.length > 0) {
          preloadProductThumbnails(originalImageUrls);
        }

        // Extract unique manufacturers
        const uniqueManufacturers = Array.from(
          new Set(transformedProducts.map((product) => product.manufacturer).filter(Boolean))
        );
        setManufacturers(uniqueManufacturers);

        // Extract unique years and add "Older" option
        const uniqueYears = Array.from(
          new Set(
            transformedProducts
              .filter((product) => product.year)
              .map((product) => product.year as number)
          )
        ).sort((a, b) => b - a);
        setYears([...uniqueYears, "Older"]);

        // Find max price for slider
        const highestPrice = Math.max(
          ...transformedProducts.map((product) => Number(product.price))
        );
        setPriceRange([0, highestPrice > 0 ? highestPrice : 1000000]);

        // Track category visit for analytics
        try {
          await trackCategoryVisit({
            categoryId: foundCategory.id,
            subcategoryId: foundSubCategory.id,
            productCategoryId: foundProductCategory.id,
            visitType: 'product_category',
            source: 'navigation'
          });
        } catch (analyticsError) {
          console.error('Error tracking category visit:', analyticsError);
        }

      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categoryId, subCategoryId, productCategoryId]);

  useEffect(() => {
    applyFilters();
  }, [
    searchQuery,
    priceRange,
    selectedConditions,
    selectedManufacturers,
    selectedYear,
    location,
    availability,
  ]);

  const applyFilters = () => {
    let filtered = [...products];

    // Apply search term filter
    if (searchQuery) {
      const term = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(term) ||
          (product.description && product.description.toLowerCase().includes(term)) ||
          (product.manufacturer && product.manufacturer.toLowerCase().includes(term)) ||
          (product.model && product.model.toLowerCase().includes(term))
      );
    }

    // Apply price range filter
    filtered = filtered.filter(
      (product) =>
        Number(product.price) >= priceRange[0] && Number(product.price) <= priceRange[1]
    );

    // Apply condition filter
    if (selectedConditions.length > 0) {
      filtered = filtered.filter((product) =>
        selectedConditions.includes(product.condition)
      );
    }

    // Apply manufacturer filter
    if (selectedManufacturers.length > 0) {
      filtered = filtered.filter((product) =>
        product.manufacturer && selectedManufacturers.includes(product.manufacturer)
      );
    }

    // Apply year filter
    if (selectedYear) {
      if (selectedYear === "Older") {
        filtered = filtered.filter(
          (product) => product.year && product.year < 2015
        );
      } else {
        const year = parseInt(selectedYear);
        filtered = filtered.filter((product) => product.year === year);
      }
    }

    // Apply location filter
    if (location) {
      const locationTerm = location.toLowerCase();
      filtered = filtered.filter((product) =>
        product.location && product.location.toLowerCase().includes(locationTerm)
      );
    }

    // Apply availability filters
    if (availability.length > 0) {
      filtered = filtered.filter((product) => {
        return availability.every((option) => {
          switch (option) {
            case "in_stock":
              return product.inStock;
            case "delivery":
              return product.deliveryAvailable;
            case "financing":
              return product.financing;
            default:
              return true;
          }
        });
      });
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "price_low":
          return Number(a.price) - Number(b.price);
        case "price_high":
          return Number(b.price) - Number(a.price);
        case "rating":
          return (b.rating || 0) - (a.rating || 0);
        case "newest":
          return new Date(b.listedDate || 0).getTime() - new Date(a.listedDate || 0).getTime();
        case "featured":
        default:
          // Featured products first, then by rating
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return (b.rating || 0) - (a.rating || 0);
      }
    });

    setFilteredProducts(filtered);
  };

  const resetFilters = () => {
    setSearchQuery("");
    setSelectedConditions([]);
    setSelectedManufacturers([]);
    setSelectedYear("");
    setLocation("");
    setAvailability([]);
    setPriceRange([0, Math.max(...products.map((product) => Number(product.price)))]);
    setSortBy("featured");
  };

  const toggleCondition = (condition: string) => {
    setSelectedConditions((prev) =>
      prev.includes(condition)
        ? prev.filter((c) => c !== condition)
        : [...prev, condition]
    );
  };

  const toggleManufacturer = (manufacturer: string) => {
    setSelectedManufacturers((prev) =>
      prev.includes(manufacturer)
        ? prev.filter((m) => m !== manufacturer)
        : [...prev, manufacturer]
    );
  };

  const toggleAvailability = (option: string) => {
    setAvailability((prev) =>
      prev.includes(option)
        ? prev.filter((a) => a !== option)
        : [...prev, option]
    );
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Create breadcrumb items
  const breadcrumbItems = [
    { name: "Home", href: "/" },
            { name: "Products", href: "/product" },
  ];

  if (category) {
    breadcrumbItems.push({
      name: category.name,
      href: `/${category.slug}`,
    });
  }
  if (subCategory) {
    breadcrumbItems.push({
      name: subCategory.name,
      href: `/${category?.slug}/${subCategory.slug}`,
    });
  }
  if (productCategory) {
    breadcrumbItems.push({
      name: productCategory.name,
      href: `/${category?.slug}/${subCategory?.slug}/${productCategory.slug}`,
    });
  }

  const activeFiltersCount =
    (selectedConditions.length > 0 ? 1 : 0) +
    (selectedManufacturers.length > 0 ? 1 : 0) +
    (selectedYear ? 1 : 0) +
    (location ? 1 : 0) +
    (availability.length > 0 ? 1 : 0) +
    (priceRange[0] > 0 || priceRange[1] < 1000000 ? 1 : 0);

  const filtersSidebar = (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Filters</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setSelectedConditions([]);
            setSelectedManufacturers([]);
            setSelectedYear("");
            setLocation("");
            setAvailability([]);
            setPriceRange([0, 1000000]);
          }}
        >
          Clear All
        </Button>
      </div>

      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="condition">
          <AccordionTrigger>Condition</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2">
              {["New", "Used", "Refurbished"].map((condition) => (
                <div key={condition} className="flex items-center space-x-2">
                  <Checkbox
                    id={`condition-${condition}`}
                    checked={selectedConditions.includes(condition)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedConditions([...selectedConditions, condition]);
                      } else {
                        setSelectedConditions(
                          selectedConditions.filter((c) => c !== condition)
                        );
                      }
                    }}
                  />
                  <label
                    htmlFor={`condition-${condition}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {condition}
                  </label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Add other filter sections here */}
      </Accordion>
    </div>
  );

  const headerContent = (
    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
      <div className="flex-1 w-full sm:w-auto">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="search"
            placeholder="Search products..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      <div className="w-full sm:w-auto">
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger>
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="featured">Featured</SelectItem>
            <SelectItem value="price-low">Price: Low to High</SelectItem>
            <SelectItem value="price-high">Price: High to Low</SelectItem>
            <SelectItem value="newest">Newest First</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  if (loading) {
    return (
      <PageLayout
        title="Loading..."
        description="Please wait while we load the products."
      >
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-3/4"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-64 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!category || !subCategory || !productCategory) {
    return (
      <PageLayout
        title="Category Not Found"
        description="The category you are looking for does not exist."
      >
        <Button onClick={() => window.history.back()}>Go Back</Button>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      breadcrumbItems={breadcrumbItems}
      title={productCategory.name}
      description={productCategory.description}
      headerContent={headerContent}
      sidebar={filtersSidebar}
      sidebarPosition="left"
      sidebarWidth="md"
    >
      {/* Mobile Filter Button */}
      {isTablet && (
        <div className="mb-6">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FilterIcon className="mr-2 h-4 w-4" />
            {showFilters ? "Hide Filters" : "Show Filters"}
            {activeFiltersCount > 0 && (
              <Badge className="ml-2" variant="secondary">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        </div>
      )}

      {/* Mobile Filters */}
      {isTablet && showFilters && (
        <div className="mb-6 p-4 border rounded-lg">
          {filtersSidebar}
        </div>
      )}

      {/* Product Grid */}
      <ProductGrid
        products={filteredProducts}
        title={productCategory.name}
        showFilters={false}
        enableDirectQuotes={true}
      />
    </PageLayout>
  );
}
